/**
 * StationSettingsSection — org-wide station toggles (extracted from the old
 * god component's `renderStationSettings`).
 *
 * CANONICAL EXEMPLAR of the Console dirty-model for sections:
 * 1. Hold pending edits locally (`pending` partial over the loaded values).
 * 2. Compute `isDirty` against the server values.
 * 3. `useDirtyRegistration(key, isDirty ? { label, save, discard } : null)` —
 *    `save` uses the underlying mutation and THROWS on failure; `discard`
 *    clears pending. No inline Save buttons, no autosave-on-toggle.
 */
import { useState } from 'react';
import { Settings } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useStationSettings, type StationSettings } from '../hooks/useStationSettings';
import { SettingsSection } from '../components/console/SettingsSection';
import { SettingRow, SettingRowDivider } from '../components/console/SettingRow';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

type StationKey = keyof StationSettings;

const ROWS: Array<{
  key: StationKey;
  id: string;
  label: string;
  onDescription: string;
  offDescription: string;
  bullets: string[];
}> = [
  {
    key: 'pretreatStationEnabled',
    id: 'pretreat-enabled',
    label: 'Pretreat station',
    onDescription: 'Active: dark garments are scanned before printing.',
    offDescription: 'Disabled: garments skip the pretreat step.',
    bullets: [
      'When enabled: Pretreat nav item visible, garments can be scanned into pretreat',
      'When disabled: Pretreat nav hidden, API rejects pretreat scans',
    ],
  },
  {
    key: 'packerPriorityEnabled',
    id: 'packer-priority',
    label: 'Packer priority system',
    onDescription: 'Priority badges visible: bins show urgency indicators.',
    offDescription: 'Priority badges hidden: standard bin display only.',
    bullets: [
      'When enabled: green/yellow/red priority based on bin occupancy thresholds',
      'When disabled: standard bin list without urgency highlighting',
    ],
  },
  {
    key: 'defaultSkipVerification',
    id: 'default-skip-verification',
    label: 'Skip item verification (default)',
    onDescription: 'Verification skipped by default: items auto-verify on scan.',
    offDescription: 'Verification required (default): packers must scan each item.',
    bullets: [
      'Applies to packers not assigned to a specific station',
      'Individual stations can override this under Packing stations',
    ],
  },
  {
    key: 'dtfEnabled',
    id: 'dtf-enabled',
    label: 'DTF / film stations',
    onDescription: 'DTF active: the DTF section is visible in the production nav.',
    offDescription: 'DTF disabled: the DTF section is hidden from the production nav.',
    bullets: [
      'When enabled: Film Print, Film Pre-Print, Pressing and Film Storage appear in the production nav',
      'When disabled: those stations are hidden from the production nav',
    ],
  },
];

export function StationSettingsSection() {
  const { settings, updateSettings, isUpdating } = useStationSettings();
  const [pending, setPending] = useState<Partial<Record<StationKey, boolean>>>({});

  const dirtyEntries = (Object.entries(pending) as Array<[StationKey, boolean]>).filter(
    ([key, value]) => value !== settings[key],
  );
  const isDirty = dirtyEntries.length > 0;

  useDirtyRegistration(
    'stations',
    isDirty
      ? {
          label: 'Stations',
          save: async () => {
            const updates = Object.fromEntries(dirtyEntries) as Partial<StationSettings>;
            const result = await updateSettings(updates);
            if (!result.success) {
              throw new Error(result.error || 'Failed to save station settings');
            }
            setPending({});
          },
          discard: () => setPending({}),
        }
      : null,
  );

  const valueOf = (key: StationKey): boolean => pending[key] ?? settings[key];

  return (
    <SettingsSection
      id="stations"
      title="Stations"
      description="Which stations run on the floor and how packers verify items."
      icon={Settings}
      scope="org"
    >
      <div className="flex flex-col">
        {ROWS.map((row, i) => (
          <div key={row.key}>
            {i > 0 ? <SettingRowDivider /> : null}
            <SettingRow
              htmlFor={row.id}
              label={row.label}
              description={valueOf(row.key) ? row.onDescription : row.offDescription}
              bullets={row.bullets}
            >
              <Switch
                id={row.id}
                checked={valueOf(row.key)}
                onCheckedChange={(checked) => setPending((p) => ({ ...p, [row.key]: checked }))}
                disabled={isUpdating}
                aria-describedby={`${row.id}-status`}
              />
              <span id={`${row.id}-status`} className="sr-only">
                {valueOf(row.key) ? 'Currently enabled' : 'Currently disabled'}
              </span>
            </SettingRow>
          </div>
        ))}
      </div>
    </SettingsSection>
  );
}
